// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { caseInsensitiveEnum } from '@/constants/case-insensitive-enum';
import { MESSAGE_TYPES, MESSAGE_TYPE_LABELS } from '@/constants/message-type';
import { z } from 'zod';

// An audit entry names the message type as it was called when the change
// happened, and a retired name is never rewritten. These are the digest topics
// that existed before the inbox, kept here so an entry naming one still reads
// as words rather than failing the whole delta's parse.
const RETIRED_TOPICS = [
  'ASSET_SHIP',
  'ASSET_DELIVERY',
  'ASSET_EXPECTED_PAST_DUE_SHIP',
  'ASSET_EXPECTED_PAST_DUE_DELIVER',
  'ASSET_ASSIGNMENT_ALREADY_ASSIGNED',
  'ASSET_ASSIGNMENT_UNABLE_TO_ASSIGN',
  'ASSET_ASSIGNMENT_IMPERFECT_ASSIGNMENT',
  'ASSET_ASSIGNMENT_NO_PROJECT_FOUND',
] as const;

const RETIRED_TOPIC_LABELS: Record<(typeof RETIRED_TOPICS)[number], string> = {
  ASSET_SHIP: 'Asset shipment',
  ASSET_DELIVERY: 'Asset delivery',
  ASSET_EXPECTED_PAST_DUE_SHIP: 'Delayed shipment',
  ASSET_EXPECTED_PAST_DUE_DELIVER: 'Delayed delivery',
  ASSET_ASSIGNMENT_ALREADY_ASSIGNED:
    'Asset assignment warnings: already assigned',
  ASSET_ASSIGNMENT_UNABLE_TO_ASSIGN:
    'Asset assignment warnings: unable to assign',
  ASSET_ASSIGNMENT_IMPERFECT_ASSIGNMENT:
    'Asset assignment information: imperfect assignments',
  ASSET_ASSIGNMENT_NO_PROJECT_FOUND:
    'Asset assignment information: no projects found',
};

// Old entries were written in the stored lowercase form, newer ones in the
// wire's uppercase, so the schema reads either.
export const auditedMessageTypeSchema = caseInsensitiveEnum([
  ...MESSAGE_TYPES,
  ...RETIRED_TOPICS,
]);

export type AuditedMessageType = z.infer<typeof auditedMessageTypeSchema>;

const AUDITED_MESSAGE_TYPE_LABELS: Record<AuditedMessageType, string> = {
  ...MESSAGE_TYPE_LABELS,
  ...RETIRED_TOPIC_LABELS,
};

/** Names a message type an audit entry recorded, retired ones included. */
export function toAuditedMessageTypeLabel(
  messageType: AuditedMessageType
): string {
  return AUDITED_MESSAGE_TYPE_LABELS[messageType];
}
